import { useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d7afnf9nauq9gnuqnhr0';

export default function ContactSection() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('sending');
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-28 px-8 md:px-20 bg-stone-900">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-16">
          <p className="text-stone-400 text-sm uppercase tracking-widest mb-4">Get in Touch</p>
          <h2
            className="text-4xl md:text-5xl font-light text-white"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Contact Us
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact info */}
          <div className="flex flex-col gap-10">
            <p className="text-stone-400 text-base leading-relaxed">
              Have a question about our products or want to learn more about OSMICA, d.o.o.?
              We&apos;d love to hear from you. Reach out through any of the channels below.
            </p>

            <div className="flex flex-col gap-8">
              {/* Address */}
              <div className="flex gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-stone-800 text-stone-300 shrink-0">
                  <i className="ri-map-pin-line" />
                </div>
                <div>
                  <p className="text-stone-500 text-xs uppercase tracking-widest mb-1">Address</p>
                  <p className="text-white text-sm leading-relaxed">
                    Koroska cesta 44<br />
                    Velenje 3320<br />
                    Slovenia
                  </p>
                </div>
              </div>

              {/* Email */}
              <div className="flex gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-stone-800 text-stone-300 shrink-0">
                  <i className="ri-mail-line" />
                </div>
                <div>
                  <p className="text-stone-500 text-xs uppercase tracking-widest mb-1">Email</p>
                  <p className="text-white text-sm">info@osmicadoo.com</p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-stone-800 text-stone-300 shrink-0">
                  <i className="ri-phone-line" />
                </div>
                <div>
                  <p className="text-stone-500 text-xs uppercase tracking-widest mb-1">Phone</p>
                  <p className="text-white text-sm">+1 7405307847</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact form */}
          <div>
            <form
              data-readdy-form
              id="contact-form"
              onSubmit={handleSubmit}
              className="flex flex-col gap-5"
            >
              <div className="flex flex-col gap-1.5">
                <label className="text-stone-400 text-xs uppercase tracking-widest">Name</label>
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="Your full name"
                  className="bg-stone-800 border border-stone-700 rounded-lg px-4 py-3 text-white text-sm placeholder-stone-500 focus:outline-none focus:border-stone-500 transition-colors"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-stone-400 text-xs uppercase tracking-widest">Email</label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="your@email.com"
                  className="bg-stone-800 border border-stone-700 rounded-lg px-4 py-3 text-white text-sm placeholder-stone-500 focus:outline-none focus:border-stone-500 transition-colors"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-stone-400 text-xs uppercase tracking-widest">Message</label>
                <textarea
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="How can we help you?"
                  className="bg-stone-800 border border-stone-700 rounded-lg px-4 py-3 text-white text-sm placeholder-stone-500 focus:outline-none focus:border-stone-500 transition-colors resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={status === 'sending'}
                className="bg-white text-stone-900 rounded-lg px-6 py-3 text-sm font-medium hover:bg-stone-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {status === 'sending' ? 'Sending...' : 'Send Message'}
              </button>

              {status === 'success' && (
                <p className="text-green-400 text-sm text-center">
                  Message sent successfully. We&apos;ll be in touch soon!
                </p>
              )}
              {status === 'error' && (
                <p className="text-red-400 text-sm text-center">
                  Something went wrong. Please try again or email us directly.
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
